<template>
    <div class="flow-chart-container">
        <!-- Search Box -->
        <div class="search-container" v-if="allValidators.length > 0">
            <div class="search-box">
                <i class="fas fa-search search-icon"></i>
                <input
                    type="text"
                    v-model="searchQuery"
                    @focus="showSearchResults = true"
                    @blur="handleSearchBlur"
                    placeholder="Search validators by name or address..."
                    class="search-input"
                />
                <button v-if="searchQuery || activeValidator" @click="clearSearch" class="clear-search">
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <!-- Search Results Dropdown -->
            <div v-if="showSearchResults && filteredValidators.length > 0" class="search-results">
                <button
                    v-for="validator in filteredValidators.slice(0, 10)"
                    :key="validator.address"
                    @mousedown.prevent="selectValidator(validator)"
                    class="search-result-item"
                >
                    <div class="result-name">{{ validator.name }}</div>
                    <div class="result-meta">
                        <span class="result-address">{{ validator.address.substring(0, 8) }}...</span>
                        <span class="result-volume">{{ formatSOL(validator.total, true) }}</span>
                    </div>
                </button>
            </div>
        </div>

        <!-- Source Validators -->
        <div class="validators-column source-column" ref="sourceColumn">
            <div class="column-header">Source</div>
            <div class="validators-list">
                <button
                    v-for="source in sortedSources"
                    :key="source.address"
                    :ref="el => setSourceRef(el, source.address)"
                    :class="['validator-button', {
                        'highlighted': isHighlighted(source.address, 'source'),
                        'dimmed': isDimmed(source.address, 'source')
                    }]"
                    @mouseenter="highlightFlows(source.address, 'source')"
                    @mouseleave="clearHighlight"
                    @click="toggleFilter(source.address, 'source')"
                >
                    <div class="validator-info">
                        <div class="validator-name">{{ source.name }}</div>
                        <div class="validator-meta">
                            <span class="amount">
                                <span class="filtered-amount">{{ formatSOL(source.filteredTotal, true) }}</span>
                                <span v-if="source.filteredTotal !== source.total" class="total-amount">
                                    / {{ formatSOL(source.total, true) }}
                                </span>
                            </span>
                            <span class="percentage">
                                <span class="filtered-percentage">{{ source.filteredPercentage.toFixed(1) }}%</span>
                                <span v-if="Math.abs(source.filteredPercentage - source.percentage) > 0.1" class="total-percentage">
                                    / {{ source.percentage.toFixed(1) }}%
                                </span>
                            </span>
                        </div>
                    </div>
                </button>
            </div>
        </div>

        <!-- Flow SVG -->
        <div class="flow-svg-container" ref="svgContainer">
            <svg :width="svgWidth" :height="svgHeight" class="flow-svg">
                <defs>
                    <linearGradient
                        v-for="flow in visibleFlows"
                        :key="`gradient-${flow.id}`"
                        :id="`gradient-${flow.id}`"
                        x1="0%" y1="0%" x2="100%" y2="0%"
                    >
                        <stop offset="0%" :stop-color="getFlowColor(flow, 'start')" stop-opacity="0.6"/>
                        <stop offset="100%" :stop-color="getFlowColor(flow, 'end')" stop-opacity="0.6"/>
                    </linearGradient>
                </defs>

                <!-- Invisible hit area for easier hovering -->
                <path
                    v-for="flow in visibleFlows"
                    :key="`hit-${flow.id}`"
                    :d="generatePath(flow)"
                    stroke="transparent"
                    :stroke-width="Math.max(getStrokeWidth(flow), 12)"
                    fill="none"
                    class="flow-hit-area"
                    @mouseenter="highlightFlow(flow)"
                    @mouseleave="clearHighlight"
                >
                    <title>{{ getFlowTooltip(flow) }}</title>
                </path>

                <!-- Visible flow path -->
                <path
                    v-for="flow in visibleFlows"
                    :key="flow.id"
                    :d="generatePath(flow)"
                    :stroke="`url(#gradient-${flow.id})`"
                    :stroke-width="getStrokeWidth(flow)"
                    :class="['flow-path', {
                        'highlighted': isFlowHighlighted(flow),
                        'dimmed': highlightedFlow && !isFlowHighlighted(flow)
                    }]"
                    fill="none"
                    pointer-events="none"
                />
            </svg>
        </div>

        <!-- Target Validators -->
        <div class="validators-column target-column">
            <div class="column-header">Target</div>
            <div class="validators-list">
                <button
                    v-for="target in sortedTargets"
                    :key="target.address"
                    :ref="el => setTargetRef(el, target.address)"
                    :class="['validator-button', {
                        'highlighted': isHighlighted(target.address, 'target'),
                        'dimmed': isDimmed(target.address, 'target')
                    }]"
                    @mouseenter="highlightFlows(target.address, 'target')"
                    @mouseleave="clearHighlight"
                    @click="toggleFilter(target.address, 'target')"
                >
                    <div class="validator-info">
                        <div class="validator-name">{{ target.name }}</div>
                        <div class="validator-meta">
                            <span class="amount">
                                <span class="filtered-amount">{{ formatSOL(target.filteredTotal, true) }}</span>
                                <span v-if="target.filteredTotal !== target.total" class="total-amount">
                                    / {{ formatSOL(target.total, true) }}
                                </span>
                            </span>
                            <span class="percentage">
                                <span class="filtered-percentage">{{ target.filteredPercentage.toFixed(1) }}%</span>
                                <span v-if="Math.abs(target.filteredPercentage - target.percentage) > 0.1" class="total-percentage">
                                    / {{ target.percentage.toFixed(1) }}%
                                </span>
                            </span>
                        </div>
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FlowChart',
    props: {
        flows: {
            type: Array,
            required: true
        },
        height: {
            type: Number,
            default: 900
        }
    },
    data() {
        return {
            svgWidth: 600,
            svgHeight: 900,
            highlightedSource: null,
            highlightedTarget: null,
            highlightedFlow: null,
            activeSource: null,
            activeTarget: null,
            activeValidator: null, // For search-based filter (shows flows from OR to)
            sourceRefs: {},
            targetRefs: {},
            sourcePositions: {},
            targetPositions: {},
            searchQuery: '',
            showSearchResults: false,
            isUpdatingPositions: false // Flag to prevent recursion
        };
    },
    computed: {
        // Filter flows based on active source/target/validator
        filteredFlows() {
            if (this.activeValidator) {
                // Show all flows FROM or TO this validator
                return this.flows.filter(f => f.from === this.activeValidator || f.to === this.activeValidator);
            }
            if (this.activeSource) {
                return this.flows.filter(f => f.from === this.activeSource);
            }
            if (this.activeTarget) {
                return this.flows.filter(f => f.to === this.activeTarget);
            }
            return this.flows;
        },

        // All unique validators from both sources and targets
        allValidators() {
            const validators = new Map();

            // Add all sources
            Object.values(this.sourceAggregates).forEach(source => {
                validators.set(source.address, {
                    address: source.address,
                    name: source.name,
                    total: source.total
                });
            });

            // Add all targets (merge with sources if they exist in both)
            Object.values(this.targetAggregates).forEach(target => {
                if (validators.has(target.address)) {
                    const existing = validators.get(target.address);
                    existing.total += target.total;
                } else {
                    validators.set(target.address, {
                        address: target.address,
                        name: target.name,
                        total: target.total
                    });
                }
            });

            return Array.from(validators.values()).sort((a, b) => b.total - a.total);
        },

        // Filtered validators based on search query
        filteredValidators() {
            if (!this.searchQuery) return [];

            const query = this.searchQuery.toLowerCase();
            return this.allValidators.filter(v => {
                return v.name.toLowerCase().includes(query) ||
                       v.address.toLowerCase().includes(query);
            });
        },

        // Track which validators are in the filtered view
        activeSourceAddresses() {
            return new Set(this.filteredFlows.map(f => f.from));
        },

        activeTargetAddresses() {
            return new Set(this.filteredFlows.map(f => f.to));
        },

        // Aggregate flows by source (from ALL flows for full context)
        sourceAggregates() {
            const aggregates = {};
            this.flows.forEach(flow => {
                if (!aggregates[flow.from]) {
                    aggregates[flow.from] = {
                        address: flow.from,
                        name: flow.from_name,
                        total: 0,
                        flows: []
                    };
                }
                aggregates[flow.from].total += flow.flow;
                aggregates[flow.from].flows.push(flow);
            });
            return aggregates;
        },

        // Aggregate flows by target (from ALL flows for full context)
        targetAggregates() {
            const aggregates = {};
            this.flows.forEach(flow => {
                if (!aggregates[flow.to]) {
                    aggregates[flow.to] = {
                        address: flow.to,
                        name: flow.to_name,
                        total: 0,
                        flows: []
                    };
                }
                aggregates[flow.to].total += flow.flow;
                aggregates[flow.to].flows.push(flow);
            });
            return aggregates;
        },

        // Calculate total volume for percentages (based on ALL flows)
        totalVolume() {
            return this.flows.reduce((sum, flow) => sum + flow.flow, 0);
        },

        // Sort sources by: 1) active filter first, 2) has filtered flows, 3) total volume
        sortedSources() {
            return Object.values(this.sourceAggregates)
                .map((source) => {
                    // Calculate filtered total for this source
                    const filteredTotal = this.filteredFlows
                        .filter(f => f.from === source.address)
                        .reduce((sum, f) => sum + f.flow, 0);

                    return {
                        ...source,
                        filteredTotal,
                        percentage: (source.total / this.totalVolume) * 100,
                        filteredPercentage: this.activeTarget
                            ? (filteredTotal / source.total) * 100
                            : (filteredTotal / this.filteredFlows.reduce((s, f) => s + f.flow, 0)) * 100,
                        isActive: this.activeSource === source.address || this.activeValidator === source.address,
                        hasFilteredFlows: filteredTotal > 0
                    };
                })
                .sort((a, b) => {
                    // Active source first
                    if (a.isActive) return -1;
                    if (b.isActive) return 1;
                    // Sources with filtered flows next
                    if (a.hasFilteredFlows && !b.hasFilteredFlows) return -1;
                    if (b.hasFilteredFlows && !a.hasFilteredFlows) return 1;
                    // Then by total volume
                    return b.total - a.total;
                });
        },

        // Sort targets by: 1) active filter first, 2) has filtered flows, 3) total volume
        sortedTargets() {
            return Object.values(this.targetAggregates)
                .map((target) => {
                    // Calculate filtered total for this target
                    const filteredTotal = this.filteredFlows
                        .filter(f => f.to === target.address)
                        .reduce((sum, f) => sum + f.flow, 0);

                    return {
                        ...target,
                        filteredTotal,
                        percentage: (target.total / this.totalVolume) * 100,
                        filteredPercentage: this.activeSource
                            ? (filteredTotal / target.total) * 100
                            : (filteredTotal / this.filteredFlows.reduce((s, f) => s + f.flow, 0)) * 100,
                        isActive: this.activeTarget === target.address || this.activeValidator === target.address,
                        hasFilteredFlows: filteredTotal > 0
                    };
                })
                .sort((a, b) => {
                    // Active target first
                    if (a.isActive) return -1;
                    if (b.isActive) return 1;
                    // Targets with filtered flows next
                    if (a.hasFilteredFlows && !b.hasFilteredFlows) return -1;
                    if (b.hasFilteredFlows && !a.hasFilteredFlows) return 1;
                    // Then by total volume
                    return b.total - a.total;
                });
        },

        // Map filtered flows with positions
        visibleFlows() {
            return this.filteredFlows
                .map((flow, index) => ({
                    ...flow,
                    id: `flow-${index}`,
                    sourceY: this.getSourceY(flow.from),
                    targetY: this.getTargetY(flow.to)
                }))
                .filter(flow => {
                    // Only show flows that have valid measured positions
                    // This prevents flows from rendering with undefined/null/invalid positions
                    const sourceY = flow.sourceY;
                    const targetY = flow.targetY;
                    return sourceY != null && targetY != null &&
                           typeof sourceY === 'number' && typeof targetY === 'number' &&
                           !isNaN(sourceY) && !isNaN(targetY);
                });
        }
    },
    mounted() {
        this.updateSvgSize();
        window.addEventListener('resize', this.updateSvgSize);

        // Update SVG height after DOM is ready
        this.$nextTick(() => {
            this.updateSvgHeight();
        });
    },
    updated() {
        // Only update if we're not already in the middle of updating positions
        // This prevents infinite recursion loops
        if (!this.isUpdatingPositions) {
            this.$nextTick(() => {
                this.updateSvgHeight();
            });
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateSvgSize);
    },
    watch: {
        // Re-measure positions when filters change to ensure all flows render correctly
        activeSource() {
            // Use setTimeout to wait for browser layout to complete after re-sorting
            this.$nextTick(() => {
                setTimeout(() => {
                    this.updateSvgHeight();
                }, 50);
            });
        },
        activeTarget() {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.updateSvgHeight();
                }, 50);
            });
        },
        activeValidator() {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.updateSvgHeight();
                }, 50);
            });
        }
    },
    methods: {
        setSourceRef(el, address) {
            if (el) {
                this.sourceRefs[address] = el;
            }
        },

        setTargetRef(el, address) {
            if (el) {
                this.targetRefs[address] = el;
            }
        },

        updateSvgSize() {
            if (this.$refs.svgContainer) {
                this.svgWidth = this.$refs.svgContainer.clientWidth;
            }
        },

        updateSvgHeight() {
            if (this.$refs.sourceColumn) {
                // Set SVG height to match the actual height of the source column (including header)
                this.svgHeight = this.$refs.sourceColumn.scrollHeight;
            }
            this.measureButtonPositions();
        },

        measureButtonPositions() {
            // Measure actual DOM positions of validator buttons
            const svgContainer = this.$refs.svgContainer;
            const sourceColumn = this.$refs.sourceColumn;
            if (!svgContainer || !sourceColumn) return;

            // Set flag to prevent recursion
            this.isUpdatingPositions = true;

            // Get the top positions of both containers relative to the parent
            const svgTop = svgContainer.getBoundingClientRect().top;
            const sourceTop = sourceColumn.getBoundingClientRect().top;

            // Calculate offset between source column and SVG
            const offset = sourceTop - svgTop;

            // Create new objects to ensure Vue reactivity
            const newSourcePositions = {};
            const newTargetPositions = {};

            // Measure source button positions
            Object.keys(this.sourceRefs).forEach(address => {
                const el = this.sourceRefs[address];
                if (el) {
                    const buttonRect = el.getBoundingClientRect();
                    const buttonCenter = buttonRect.top + (buttonRect.height / 2);
                    // Store center position relative to SVG top
                    newSourcePositions[address] = buttonCenter - svgTop;
                }
            });

            // Measure target button positions
            Object.keys(this.targetRefs).forEach(address => {
                const el = this.targetRefs[address];
                if (el) {
                    const buttonRect = el.getBoundingClientRect();
                    const buttonCenter = buttonRect.top + (buttonRect.height / 2);
                    // Store center position relative to SVG top
                    newTargetPositions[address] = buttonCenter - svgTop;
                }
            });

            // Replace entire objects to trigger reactivity
            this.sourcePositions = newSourcePositions;
            this.targetPositions = newTargetPositions;

            // Clear flag after Vue's next tick to allow future updates
            this.$nextTick(() => {
                this.isUpdatingPositions = false;
            });
        },

        getSourceY(address) {
            // Return the measured position, or undefined if not yet measured
            // This allows the filter in visibleFlows to exclude flows without positions
            return this.sourcePositions[address];
        },

        getTargetY(address) {
            // Return the measured position, or undefined if not yet measured
            // This allows the filter in visibleFlows to exclude flows without positions
            return this.targetPositions[address];
        },

        generatePath(flow) {
            const startX = 0;
            const endX = this.svgWidth;
            let startY = flow.sourceY;
            let endY = flow.targetY;
            const controlX = this.svgWidth / 2;

            // If the flow is perfectly horizontal, add a slight curve to make it visible
            // This happens when both source and target are at the top after filtering
            if (Math.abs(startY - endY) < 2) {
                // Add a slight downward curve for horizontal flows
                const midY = (startY + endY) / 2 + 20; // Dip down 20px
                return `M ${startX},${startY} Q ${controlX},${midY} ${endX},${endY}`;
            }

            // Cubic Bezier curve for smooth flow
            return `M ${startX},${startY} C ${controlX},${startY} ${controlX},${endY} ${endX},${endY}`;
        },

        getStrokeWidth(flow) {
            // Scale stroke width based on flow amount
            const minWidth = 2;
            const maxWidth = 40;
            const percentage = flow.flow / this.totalVolume;
            return Math.max(minWidth, percentage * 1000);
        },

        getFlowColor(flow, position) {
            // Color based on type of flow
            if (flow.from_name === '🆕 New Stake') {
                return position === 'start' ? '#10b981' : '#8b5cf6'; // green to purple
            } else if (flow.to_name === '🚪 Exited') {
                return position === 'start' ? '#8b5cf6' : '#ef4444'; // purple to red
            }
            return position === 'start' ? '#8b5cf6' : '#ec4899'; // purple to pink
        },

        highlightFlows(address, type) {
            if (type === 'source') {
                this.highlightedSource = address;
            } else {
                this.highlightedTarget = address;
            }
        },

        highlightFlow(flow) {
            // When hovering a flow directly, highlight that flow and its buttons
            this.highlightedFlow = flow.id;
            this.highlightedSource = flow.from;
            this.highlightedTarget = flow.to;
        },

        clearHighlight() {
            this.highlightedSource = null;
            this.highlightedTarget = null;
            this.highlightedFlow = null;
        },

        isHighlighted(address, type) {
            if (type === 'source') {
                return this.highlightedSource === address || this.activeSource === address;
            }
            return this.highlightedTarget === address || this.activeTarget === address;
        },

        isDimmed(address, type) {
            // Don't dim if hovering
            if (type === 'source' && this.highlightedSource === address) return false;
            if (type === 'target' && this.highlightedTarget === address) return false;

            // Don't dim if it's the active one
            if (type === 'source' && this.activeSource === address) return false;
            if (type === 'target' && this.activeTarget === address) return false;

            // Dim if not in the filtered flows
            if (type === 'source') {
                return !this.activeSourceAddresses.has(address);
            }
            return !this.activeTargetAddresses.has(address);
        },

        isFlowHighlighted(flow) {
            // If a specific flow is hovered, only highlight that one
            if (this.highlightedFlow) {
                return this.highlightedFlow === flow.id;
            }
            // If a button is hovered, highlight all flows from/to it
            if (this.highlightedSource && flow.from === this.highlightedSource) return true;
            if (this.highlightedTarget && flow.to === this.highlightedTarget) return true;
            return false;
        },

        toggleFilter(address, type) {
            if (type === 'source') {
                // Toggle: if clicking the same source, clear it
                if (this.activeSource === address) {
                    this.activeSource = null;
                    this.$emit('filter-changed', null);
                } else {
                    this.activeSource = address;
                    this.activeTarget = null; // Clear target filter
                    this.activeValidator = null; // Clear search filter
                    this.searchQuery = ''; // Clear search box

                    // Find the name for this address
                    const source = this.sortedSources.find(s => s.address === address);
                    this.$emit('filter-changed', {
                        type: 'source',
                        address: address,
                        name: source?.name || address
                    });

                    // Scroll parent container to top
                    this.$nextTick(() => {
                        const container = this.$el;
                        if (container) {
                            container.scrollTop = 0;
                        }
                    });
                }
            } else {
                // Toggle: if clicking the same target, clear it
                if (this.activeTarget === address) {
                    this.activeTarget = null;
                    this.$emit('filter-changed', null);
                } else {
                    this.activeTarget = address;
                    this.activeSource = null; // Clear source filter
                    this.activeValidator = null; // Clear search filter
                    this.searchQuery = ''; // Clear search box

                    // Find the name for this address
                    const target = this.sortedTargets.find(t => t.address === address);
                    this.$emit('filter-changed', {
                        type: 'target',
                        address: address,
                        name: target?.name || address
                    });

                    // Scroll parent container to top
                    this.$nextTick(() => {
                        const container = this.$el;
                        if (container) {
                            container.scrollTop = 0;
                        }
                    });
                }
            }
        },

        getBiggestSource() {
            if (this.flows.length === 0) return null;

            const sourceVolumes = {};
            this.flows.forEach(flow => {
                sourceVolumes[flow.from] = (sourceVolumes[flow.from] || 0) + flow.flow;
            });

            return Object.keys(sourceVolumes).reduce((a, b) =>
                sourceVolumes[a] > sourceVolumes[b] ? a : b
            );
        },

        getFlowTooltip(flow) {
            return `${flow.from_name} → ${flow.to_name}\n${this.formatSOL(flow.flow, true)}\n${flow.count} movements`;
        },

        formatSOL(amount, isAlreadySol = false) {
            if (!amount) return '0 SOL';
            const sol = isAlreadySol ? amount : amount / 1e9;
            return `${sol.toLocaleString(undefined, { maximumFractionDigits: 0 })} SOL`;
        },

        selectValidator(validator) {
            this.searchQuery = validator.name;
            this.activeValidator = validator.address;
            this.activeSource = null;
            this.activeTarget = null;
            this.showSearchResults = false;

            // Emit filter change
            this.$emit('filter-changed', {
                type: 'validator',
                address: validator.address,
                name: validator.name
            });

            // Scroll parent container to top
            this.$nextTick(() => {
                const container = this.$el;
                if (container) {
                    container.scrollTop = 0;
                }
            });
        },

        clearSearch() {
            this.searchQuery = '';
            this.activeValidator = null;
            this.showSearchResults = false;
            this.$emit('filter-changed', null);
        },

        handleSearchBlur() {
            // Delay to allow click on search results
            setTimeout(() => {
                this.showSearchResults = false;
            }, 200);
        }
    }
};
</script>

<style scoped>
.flow-chart-container {
    display: grid;
    grid-template-columns: 280px 1fr 280px;
    gap: 1rem;
    max-height: 900px;
    overflow-y: auto;
    background: linear-gradient(to bottom, rgba(139, 92, 246, 0.05), rgba(236, 72, 153, 0.05));
    border-radius: 8px;
    padding: 1.5rem;
}

.search-container {
    grid-column: 1 / -1;
    position: relative;
    margin-bottom: 1rem;
}

.search-box {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 1rem;
    color: rgba(139, 92, 246, 0.6);
    pointer-events: none;
}

.search-input {
    width: 100%;
    padding: 0.75rem 3rem 0.75rem 2.5rem;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(139, 92, 246, 0.3);
    border-radius: 8px;
    color: #1a1a2e;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.1);
}

.search-input:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.8);
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
}

.search-input::placeholder {
    color: rgba(26, 26, 46, 0.5);
}

.clear-search {
    position: absolute;
    right: 0.5rem;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 4px;
    transition: color 0.2s ease;
}

.clear-search:hover {
    color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.05);
}

.search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 0.5rem;
    background: rgba(30, 30, 40, 0.98);
    border: 1px solid rgba(139, 92, 246, 0.3);
    border-radius: 8px;
    max-height: 400px;
    overflow-y: auto;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.search-result-item {
    width: 100%;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
}

.search-result-item:hover {
    background: rgba(139, 92, 246, 0.2);
}

.search-result-item:last-child {
    border-bottom: none;
}

.result-name {
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
}

.result-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
}

.result-address {
    color: rgba(255, 255, 255, 0.5);
    font-family: monospace;
}

.result-volume {
    color: rgba(139, 92, 246, 0.8);
    font-weight: 600;
}

.validators-column {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.column-header {
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.7;
    margin-bottom: 0.5rem;
}

.validators-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.validator-button {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 0.75rem;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
    text-align: left;
}

.validator-button:hover,
.validator-button.highlighted {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.5);
    transform: translateX(4px);
}

.validator-button.dimmed {
    opacity: 0.3;
    filter: grayscale(0.5);
}

.validator-button.dimmed:hover {
    opacity: 0.7;
    filter: grayscale(0);
}

.target-column .validator-button:hover,
.target-column .validator-button.highlighted {
    transform: translateX(-4px);
}

.validator-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.validator-name {
    font-weight: 500;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.validator-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
}

.amount {
    color: rgba(139, 92, 246, 1);
    font-weight: 600;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
}

.amount .total-amount {
    opacity: 0.5;
    font-size: 0.7rem;
    font-weight: 400;
}

.percentage {
    opacity: 0.6;
    font-family: monospace;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    text-align: right;
}

.percentage .total-percentage {
    opacity: 0.5;
    font-size: 0.7rem;
}

.flow-svg-container {
    position: relative;
}

.flow-svg {
    width: 100%;
    display: block;
}

.flow-hit-area {
    cursor: pointer;
}

.flow-path {
    stroke-opacity: 0.6;
}

.flow-path.highlighted {
    stroke-opacity: 1 !important;
}

.flow-path.dimmed {
    stroke-opacity: 0.1 !important;
}

/* Responsive */
@media (max-width: 1024px) {
    .flow-chart-container {
        grid-template-columns: 1fr;
        grid-template-rows: auto auto auto;
    }

    .validators-list {
        max-height: 300px;
        flex-direction: row;
        overflow-x: auto;
        overflow-y: hidden;
    }

    .validator-button {
        min-width: 200px;
    }

    .flow-svg-container {
        min-height: 600px;
    }
}
</style>
